import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useFormContext } from "react-hook-form";

import { NotetakerRecorderDisclaimerType } from "../../main/graphql";

export type DisclaimerFormData = {
  disclaimer: string;
  disclaimerType: NotetakerRecorderDisclaimerType;
  defaultDisclaimer: string;
};

const ZoomDisclaimerInputs: React.FC = () => {
  const { register, watch, setValue } = useFormContext<DisclaimerFormData>();

  const notificationType = (t: NotetakerRecorderDisclaimerType): string => {
    if (t === NotetakerRecorderDisclaimerType.Organization) {
      return "a chat message";
    }
    if (t === NotetakerRecorderDisclaimerType.Default) {
      return "a chat message";
    }
    if (t === NotetakerRecorderDisclaimerType.Native) {
      return "the Zoom recording disclaimer dialog box";
    }

    return "nothing";
  };

  const [
    notetakerRecorderDisclaimer,
    notetakerRecorderDisclaimerType,
    defaultDisclaimer,
  ] = watch(["disclaimer", "disclaimerType", "defaultDisclaimer"]);
  const currentDisclaimerType =
    notetakerRecorderDisclaimerType ?? NotetakerRecorderDisclaimerType.Default;
  const isChatDisclaimer =
    currentDisclaimerType === NotetakerRecorderDisclaimerType.Default ||
    currentDisclaimerType === NotetakerRecorderDisclaimerType.Organization ||
    currentDisclaimerType === NotetakerRecorderDisclaimerType.None;
  const [chatDisclaimerType, setChatDisclaimerType] =
    React.useState<NotetakerRecorderDisclaimerType>(
      isChatDisclaimer
        ? currentDisclaimerType
        : NotetakerRecorderDisclaimerType.Default
    );

  React.useEffect(() => {
    if (isChatDisclaimer) {
      setChatDisclaimerType(currentDisclaimerType);
    }
  }, [currentDisclaimerType, isChatDisclaimer]);

  const radioValue = isChatDisclaimer
    ? NotetakerRecorderDisclaimerType.Default
    : NotetakerRecorderDisclaimerType.Native;

  return (
    <div>
      <Heading as="h4" fontSize="md" mb={6}>
        Disclaimer
      </Heading>

      <FormControl id="notetakerRecorderDisclaimerType" mb={8}>
        <RadioGroup
          name="strategy"
          value={radioValue}
          onChange={(value) => {
            if (value === NotetakerRecorderDisclaimerType.Native) {
              setValue(
                "disclaimerType",
                NotetakerRecorderDisclaimerType.Native,
                { shouldDirty: true }
              );
            } else {
              setValue("disclaimerType", chatDisclaimerType, {
                shouldDirty: true,
              });
            }
          }}
        >
          <VStack align="start">
            <Radio value={NotetakerRecorderDisclaimerType.Default}>
              Chat Disclaimer
            </Radio>
            <Radio value={NotetakerRecorderDisclaimerType.Native}>
              Zoom Recording Disclaimer Dialog Box
            </Radio>
          </VStack>
        </RadioGroup>
        <Box p={6} mb={4} mt={4} bg="gray.100">
          <Text fontSize="xs">
            Your interviewers and candidates will see:{" "}
            <Text fontWeight={600} as="span">
              {notificationType(currentDisclaimerType)}
            </Text>
          </Text>
          {isChatDisclaimer && (
            <Text fontSize="xs">
              With the text:{" "}
              <Text fontWeight={600} as="span">
                {currentDisclaimerType ===
                NotetakerRecorderDisclaimerType.Default
                  ? defaultDisclaimer
                  : notetakerRecorderDisclaimer}
              </Text>
            </Text>
          )}
        </Box>

        {isChatDisclaimer && (
          <FormControl mb={4} mt={4}>
            <FormLabel>Chat Disclaimer Options</FormLabel>
            <Select
              mb={4}
              value={chatDisclaimerType}
              onChange={({ target }) => {
                const nextValue =
                  target.value as NotetakerRecorderDisclaimerType;
                setValue("disclaimerType", nextValue, { shouldDirty: true });
                setChatDisclaimerType(nextValue);
              }}
            >
              <option
                key="DEFAULT"
                value={NotetakerRecorderDisclaimerType.Default}
              >
                Use BrightHire&apos;s default disclaimer
              </option>
              <option
                key="ORGANIZATION"
                value={NotetakerRecorderDisclaimerType.Organization}
              >
                Create custom disclaimer text
              </option>
              <option key="NONE" value={NotetakerRecorderDisclaimerType.None}>
                Don&apos;t send a chat disclaimer
              </option>
            </Select>
            <FormHelperText>
              Choose whether to use BrightHire&apos;s default disclaimer text,
              create your own custom message, or skip sending a chat message.
            </FormHelperText>
            <FormControl
              id="zoomNotetakerRecordingDisclaimer"
              mb={4}
              mt={4}
              hidden={
                currentDisclaimerType !==
                NotetakerRecorderDisclaimerType.Organization
              }
            >
              <Input
                {...register("disclaimer")}
                placeholder="Add a custom BrightHire disclaimer"
                data-testid="notetaker-recorder-disclaimer"
              />
              <FormHelperText>
                Please note that only the first 100 characters will be shown in
                the notification, but the full text will be available in the
                chat panel.
              </FormHelperText>
            </FormControl>
          </FormControl>
        )}
      </FormControl>
    </div>
  );
};

export default ZoomDisclaimerInputs;
